import type { ChronologyFactV2 } from './fact/chronology-fact.entity.js';
import { stripTags } from '../../../utils/sanitize.js';
import { getFactSourceText } from '../../../utils/getFactSourceText.js';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter.js';
import * as crypto from 'node:crypto';

export enum ChronologyDownloadColumn {
  DateVerbatim = 'Date (verbatim)',
  DateStandardised = 'Date (standardised)',
  Fact = 'Fact',
  Relevance = 'Relevance',
  SourceText = 'Source Text',
  SourceDocument = 'Source Document',
  Significance = 'Significance',
  Id = 'Id',
  Notes = 'Notes',
  NotesDate = 'Notes Date',
  NotesBy = 'Notes By',
  VerifiedBy = 'Verified By',
  VerifiedDate = 'Verified Date',
}

/**
 * Maximum number of items allowed for synchronous (in-process) chronology downloads.
 * Exports exceeding this limit are rejected to prevent excessive resource usage on workers.
 */
export const MAX_EXPORT_ITEMS = 10_000;

// Excel cell character limit is 32,767
// use 10,000 as a (very) safe limit (plus anything anywhere near this length would be largely unreadable anyway)
const CELL_LENGTH_LIMIT = 10000;

export function prepareChronologyCSVRow(
  fact: ChronologyFactV2,
  columns: ChronologyDownloadColumn[],
): string[] {
  let sourceText = getFactSourceText(fact.fact);

  if (sourceText !== null && sourceText.length > CELL_LENGTH_LIMIT) {
    sourceText = `${sourceText.slice(0, CELL_LENGTH_LIMIT)}...`;
  }

  const columnMapping: Record<ChronologyDownloadColumn, string | Date | null | undefined> = {
    [ChronologyDownloadColumn.DateVerbatim]: fact.factDateText,
    [ChronologyDownloadColumn.DateStandardised]: fact.factDate,
    [ChronologyDownloadColumn.Fact]: fact.description,
    [ChronologyDownloadColumn.Relevance]: capitalizeFirstLetter(fact.relevance),
    [ChronologyDownloadColumn.SourceText]: stripTags(sourceText),
    [ChronologyDownloadColumn.SourceDocument]: fact.fact.locations.at(0)?.doc.file.name,
    [ChronologyDownloadColumn.Significance]: fact.significance,
    [ChronologyDownloadColumn.Id]: fact.id,
    [ChronologyDownloadColumn.Notes]: fact.fact.comments.at(0)?.content,
    [ChronologyDownloadColumn.NotesDate]: fact.fact.comments.at(0)?.createdDate,
    [ChronologyDownloadColumn.NotesBy]: fact.fact.comments.at(0)?.createdBy.email,
    [ChronologyDownloadColumn.VerifiedBy]: fact.verifiedBy?.email,
    [ChronologyDownloadColumn.VerifiedDate]: fact.verifiedDate,
  };

  return columns.map((column) => columnMapping[column]?.toString() ?? '');
}

/**
 * Extracts unique document IDs from chronology facts.
 * Each fact can reference multiple documents via locations.
 * Returns a sorted array of unique document IDs.
 */
export function extractUniqueDocumentsFromChronology(facts: ChronologyFactV2[]): string[] {
  const uniqueDocIds = new Set<string>();

  for (const fact of facts) {
    for (const location of fact.fact.locations) {
      if (location.docId) {
        uniqueDocIds.add(location.docId);
      }
    }
  }

  // Return sorted array for consistent hashing
  return Array.from(uniqueDocIds).sort();
}

/**
 * Generates an MD5 hash from a list of document IDs.
 * Used to create consistent cache keys for document sets.
 * Returns the first 8 characters of the hash (matching existing pattern).
 */
export function generateDocumentSetHash(docIds: string[]): string {
  // Sort to ensure consistent hash for same set of documents
  const sortedIds = [...docIds].sort();
  const idsString = sortedIds.join(',');

  const hash = crypto.createHash('md5').update(idsString).digest('hex');

  // Return first 8 characters to match existing pattern
  return hash.substring(0, 8);
}
